import { Check } from 'lucide-react';
import clsx from 'clsx';

interface StepDef {
  id: string;
  label: string;
  optional?: boolean;
}

interface Props {
  steps: StepDef[];
  /** Index of the currently-active step. */
  current: number;
  /** Indices of steps the user has completed (visited and validated). */
  completed: Set<number>;
  onJump: (index: number) => void;
}

/** Sticky stepper bar shown at the top of the wizard. Tappable to jump to
 *  any completed step. The current step shows a cyan filled dot; completed
 *  steps show a check; future steps show a hollow dot. */
export function Stepper({ steps, current, completed, onJump }: Props) {
  return (
    <nav
      className="sticky top-0 -mx-4 px-4 py-2.5 bg-mono-950/95 backdrop-blur border-b border-mono-800 z-10"
      aria-label="Wizard steps"
    >
      <ol className="flex items-center gap-1 overflow-x-auto no-scrollbar">
        {steps.map((s, i) => {
          const isCurrent = i === current;
          const isDone = completed.has(i);
          const canJump = isDone || i <= current;
          return (
            <li key={s.id} className="flex items-center gap-1 shrink-0">
              <button
                type="button"
                onClick={() => canJump && onJump(i)}
                disabled={!canJump}
                className={clsx(
                  'flex items-center gap-1.5 px-2 py-1 rounded-md text-[11px] tracking-wide transition-colors',
                  isCurrent && 'bg-cyan-500/15 text-cyan-200 ring-1 ring-cyan-500/40',
                  !isCurrent && isDone && 'text-emerald-300 hover:bg-mono-800',
                  !isCurrent && !isDone && 'text-mono-400',
                  !canJump && 'opacity-60',
                )}
                aria-current={isCurrent ? 'step' : undefined}
              >
                <span className={clsx(
                  'inline-flex items-center justify-center h-5 w-5 rounded-full text-[10px] font-semibold',
                  isCurrent && 'bg-cyan-500 text-mono-950',
                  !isCurrent && isDone && 'bg-emerald-500/30 text-emerald-200',
                  !isCurrent && !isDone && 'bg-mono-800 text-mono-400',
                )}>
                  {isDone && !isCurrent ? <Check className="h-3 w-3" strokeWidth={3} /> : i + 1}
                </span>
                <span className="font-medium">{s.label}</span>
                {s.optional && <span className="text-mono-500">·</span>}
              </button>
              {i < steps.length - 1 && (
                <span className="text-mono-700" aria-hidden>›</span>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
